﻿import { FieldNames, FormType } from "@/enums";
import {
  IDynamicFormField,
  IDynamicFormValue,
  IFormLabel,
  IProjectTemplate,
} from "@/interfaces";
import { useFormPropertyProvider } from "@/providers";
import {
  useCopyProjectTemplateMutation,
  useGetProjectTemplatesQuery,
} from "@/services/projectTemplate";
import {
  Checkbox,
  CheckboxProps,
  Divider,
  Form,
  Modal,
  Select,
  Spin,
} from "antd";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { CheckboxGroup } from "../styled";
type CheckboxValueType = string | number;

const { Option } = Select;
interface IProps {
  projectTemplate: IProjectTemplate;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}

interface ICategory {
  id: string;
  name: string;
  label?: IFormLabel | undefined;
  projectTypeId?: string;
}

export const ProjectTemplateCopyModal = ({
  projectTemplate,
  showModal,
  setShowModal,
}: IProps) => {
  const intl = useIntl();
  const [workingProjectType, setWorkingProjectType] = useState<string>("");
  const [otherProjectTypes, setOtherProjectTypes] = useState<ICategory[]>();
  const { getFormProperties } = useFormPropertyProvider();
  const [loading, setLoading] = useState<boolean>(false);
  const [projectTypes, setProjectTypes] = useState<IDynamicFormValue[]>([]);
  const { data: projectTemplates } = useGetProjectTemplatesQuery();

  const [copyProjectTemplate] = useCopyProjectTemplateMutation();
  const [form] = Form.useForm();
  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>([]);

  const checkboxOptions = projectTemplate.milestones?.map((milestone) => ({
    label: milestone.name,
    value: milestone.id,
  }));

  const checkboxValues = checkboxOptions?.map((a) => a.value);

  const checkAll = checkboxOptions?.length === checkedList.length;

  const onChange = (list: unknown[]) => {
    setCheckedList(list as CheckboxValueType[]);
  };

  const onCheckAllChange: CheckboxProps["onChange"] = (e) => {
    setCheckedList(e.target.checked ? checkboxValues : []);
  };

  useEffect(() => {
    const form = getFormProperties(FormType.Intake);
    const projectTypeField = form.find(
      (field: IDynamicFormField) =>
        field.property.name === FieldNames.ProjectType
    );

    if (!projectTypeField) return;

    setProjectTypes(projectTypeField.values);
  }, [getFormProperties]);

  const handleCopy = (): void => {
    setLoading(true);
    form.validateFields().then(() => {
      const milestones = form.getFieldValue("projectTemplateCheckboxes");
      const copyTo = form.getFieldValue("projectTemplateCopyTo");
      copyProjectTemplate({
        copyFrom: projectTemplate.id,
        copyTo: copyTo,
        milestones: milestones,
      })
        .unwrap()
        .then(() => {
          toast.success(
            intl.formatMessage({ id: "general.toastChangesSaved" })
          );
          setLoading(false);
          setShowModal(false);
        })
        .catch((err) => {
          toast.error(
            intl.formatMessage({ id: "general.toastSomethingWentWrong" })
          );
          setLoading(false);
        });
    });
  };

  useEffect(() => {
    if (!projectTemplates) return;

    const data: ICategory[] = [];

    projectTypes.forEach((currProjectType) => {
      const currProjectTemplate = projectTemplates.find(
        (x) => x.projectTypeId === currProjectType.id
      );

      if (!currProjectTemplate?.id) {
        console.error(
          `Project template does not exists for project type ${currProjectType.label.fallback}`
        );
        return;
      }
      if (projectTemplate.projectTypeId === currProjectTemplate.projectTypeId) {
        setWorkingProjectType(currProjectType.label.fallback);
      } else {
        data.push({
          name: currProjectType.label.fallback,
          id: currProjectTemplate.id,
        });
      }
    });

    setOtherProjectTypes(data);
  }, [projectTypes, projectTemplates, projectTemplate.projectTypeId]);

  return (
    <Modal
      title={intl.formatMessage({
        id: "projectTemplate.actions.copyTemplate",
      })}
      open={showModal}
      onCancel={(): void => setShowModal(false)}
      onOk={handleCopy}
      okButtonProps={{ disabled: loading }}
      cancelButtonProps={{ disabled: loading }}
    >
      {!loading ? (
        <Form form={form} name="projectTemplateForm">
          <FormattedMessage id="projectTemplate.actions.copy.from" />
          <p>{workingProjectType}</p>
          <Form.Item
            label={intl.formatMessage({
              id: "projectTemplate.actions.copy.selectMilestones",
            })}
          ></Form.Item>
          <Checkbox
            onChange={onCheckAllChange}
            checked={checkAll}
            data-test-id="check-all-checkbox"
          >
            {intl.formatMessage({
              id: "projectTemplate.actions.copy.checkAll",
            })}
          </Checkbox>
          <Divider />
          <Form.Item
            name="projectTemplateCheckboxes"
            required={true}
            valuePropName="checked"
          >
            <CheckboxGroup
              data-test-id={`project-template-copy-checkbox-group`}
              options={checkboxOptions}
              onChange={onChange}
              value={checkedList}
            />
          </Form.Item>
          <Form.Item
            name="projectTemplateCopyTo"
            label={intl.formatMessage({
              id: "projectTemplate.actions.copy.to",
            })}
            required={true}
          >
            <Select
              data-test-id={`project-template-copy-to`}
              style={{ width: "100%" }}
            >
              {otherProjectTypes &&
                otherProjectTypes?.map((projectType) => (
                  <Option key={projectType.id} value={projectType.id}>
                    {projectType.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      ) : (
        <Spin />
      )}
    </Modal>
  );
};
